import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from "react-router-dom";
import { connect } from "react-redux";
import * as actions from "redux/actions/reportActions";
import * as quoteActions from "redux/actions/quoteActions";
import LinearProgressBar from "components/LinearProgressBar";
import StockAvatar from "components/StockAvatar";
import { Star } from "theme/icon";
import { useReportTableSort, SortableReportTh } from '../../reportTableSort';
import { Translate } from 'react-redux-i18n';
import HeatmapModal from './HeatmapModal';

const useStyles = {
  root: {
    //  height: '100%'
  },
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  avatar: {
    marginRight: '7px',
    height: 30,
    width: 30,
    flexDirection: 'row',
    position: "absolute",
    borderRadius: "5px"
  },
};

function sumPl(items) {
  if (!Array.isArray(items) || items.length === 0) return '0.00';
  const total = items.reduce((sum, item) => sum + (Number(item.pl) || 0), 0);
  return total.toFixed(2);
}

const stylesLocal = {
  subcell: {
    padding: "5px"
  },
  cell: {
    padding: "8px"
  }
};

function barPercent(list, roi) {
  const value = Number(roi) || 0;
  const maxAbs = Math.max(...list.map((row) => Math.abs(Number(row.roi) || 0)), 1);
  return (Math.abs(value) / maxAbs) * 100;
}

function formatLastPrice(item, liveQuote) {
  const live = Number(liveQuote?.c);
  const fromLive = live > 0 ? live : Number(liveQuote?.pc);
  const fromItem = Number(item?.price ?? item?.ask);
  const value = fromLive > 0 ? fromLive : fromItem;
  if (!Number.isFinite(value) || value <= 0) {
    return '—';
  }
  return `$${value.toFixed(2)}`;
}

const Roi = props => {
  const { className, year, month, ...rest } = props;
  const { sortedList, sortKey, sortDir, onSort } = useReportTableSort(props.list, 'roi', 'desc');
  const [heatmapOpen, setHeatmapOpen] = useState(false);

  useEffect(() => {
    props.loadList(props.user_id, props.token, year, month);
  }, [props.user_id, props.token, year, month]);

  useEffect(() => {
    if (!props.user_id || !props.list?.length) {
      return;
    }
    const symbols = [...new Set(props.list.map((item) => item.symbol).filter(Boolean))];
    if (symbols.length) {
      props.loadStockQuotes(symbols, props.user_id, props.token);
    }
  }, [props.user_id, props.token, props.list]);

  return (
    <div className="report-panel" {...rest}>
      <div className="report-panel-header">
        <h3 className="report-panel-title">{"ROI P/L = " + sumPl(props.list)}</h3>
        <button
          type="button"
          className="btn btn-secondary btn-sm report-heatmap-btn"
          onClick={() => setHeatmapOpen(true)}
          disabled={!props.list?.length}
        >
          <Translate value="report_heatmap" />
        </button>
      </div>
      <div className="report-panel-body">
        <table className='table table-hover'>
          <thead>
            <tr>
              <SortableReportTh label="Symbol" sortKey="symbol" activeKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableReportTh label="Tr#" sortKey="count" activeKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableReportTh label={<Translate value="alerts_last_price" />} sortKey="price" activeKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableReportTh label="P/L" sortKey="pl" activeKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableReportTh label="%" sortKey="roi" activeKey={sortKey} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {sortedList.map((item) => (
              <tr key={item.symbol}>
                <th>
                  <div className='symbol-cell'>
                  <StockAvatar item={item} symbol={item.symbol} className="avatar-sm" />
                  <div className="symbol-meta">
                  <RouterLink to={"/stock/" + item.symbol} className="symbol-link">{item.symbol}</RouterLink>
                  {
                    item.dividend === "1" && (
                      <Star className="symbol-star" />)
                  }
                  </div>
                  </div>
                </th>
                <th style={stylesLocal.cell}>
                  {item.count}
                </th>
                <th align="right" style={stylesLocal.cell}>
                  {formatLastPrice(item, props.stock?.[item.symbol])}
                </th>
                <th align='right' style={stylesLocal.cell}>
                  ${(Number(item.pl) || 0).toFixed(2)}
                </th>
                <th style={stylesLocal.cell}>
                  <LinearProgressBar
                    percent={barPercent(sortedList, item.roi)}
                    color={Number(item.pl) >= 0 ? "green" : "red"}
                    value={(Number(item.roi) || 0).toFixed(2) + "%"}
                  />
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HeatmapModal
        open={heatmapOpen}
        list={props.list}
        onClose={() => setHeatmapOpen(false)}
      />
    </div>
  );
};

Roi.propTypes = {
  className: PropTypes.string
};


function mapStateToProps(reducer) {
  //console.log(reducer);
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.token,

    list: reducer.report.listSymbol,
    error: reducer.report.errorSymbol,
    loading: reducer.report.loadingSymbol,
    stock: reducer.event.stock,
  };
}
const mapDispatchToProps = dispatch => ({
  loadList: (user_id, token, year, month) =>
    dispatch(actions.loadListSymbol({ user_id, token, year, month })),
  loadStockQuotes: (symbols, user_id, token) =>
    dispatch(quoteActions.loadStockQuotes({ symbols, user_id, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Roi);
